<script setup>
import Loader from "@/views/components/Loader.vue";
import { ref } from "vue";
import {
  fetchInventoryReport,
  downloadInventoryReportPdf,
  downloadInventoryReportExcel,
} from "@/api/services/reports";
const filter_params = ref({});
import { ElMessage } from "element-plus";
import { formatNumber } from "../utils/number";
const tableData = ref([]);
const isLoading = ref(false);
const fetchData = async (filter, page) => {
  isLoading.value = true;
  let entity_params = {};
  try {
    if (filter === undefined) {
      entity_params = ref({});
    } else {
      entity_params = filter;
    }
    tableData.value.splice(0);
    const response = await fetchInventoryReport(entity_params, page);
    tableData.value = response.report?.map((item) => item);
  } catch (error) {
    console.log(error);
  } finally {
    isLoading.value = false;
  }
};
const downloadExcel = async () => {
  try {
    ElMessage({
      type: "info",
      message: "Your report will be downloading shortly. Please wait...",
    });
    await downloadInventoryReportExcel(filter_params)
      .then((Response) => {
        let fileURL = window.URL.createObjectURL(
          new Blob([Response], {
            type: "application/vnd.ms-excel",
          })
        );
        let fileLink = document.createElement("a");

        fileLink.href = fileURL;
        fileLink.setAttribute(
          "download",
          "inventory_report_" + Math.round(+new Date() / 1000) + ".xlsx"
        );
        document.body.appendChild(fileLink);

        fileLink.click();
      })
      .catch((err) => {
        console.log(err);
        ElMessage({
          type: "error",
          message: "Whoops! something went wrong downloading report",
        });
      });
  } catch (error) {
    console.log(error);
    ElMessage({
      type: "error",
      message: "Whoops! something went wrong downloading report",
    });
  } finally {
    isLoading.value = false;
  }
};
const downloadPdf = async () => {
  try {
    ElMessage({
      type: "info",
      message: "Your report will be downloading shortly. Please wait...",
    });
    await downloadInventoryReportPdf(filter_params)
      .then((Response) => {
        let fileURL = window.URL.createObjectURL(
          new Blob([Response], {
            type: "application/pdf",
          })
        );
        let fileLink = document.createElement("a");

        fileLink.href = fileURL;
        fileLink.setAttribute(
          "download",
          "inventory_report_" + Math.round(+new Date() / 1000) + ".pdf"
        );
        document.body.appendChild(fileLink);

        fileLink.click();
      })
      .catch((err) => {
        console.log(err);

        ElMessage({
          type: "error",
          message: "Whoops! something went wrong downloading report",
        });
      });
  } catch (error) {
    console.log(error);
    ElMessage({
      type: "error",
      message: "Whoops! something went wrong downloading report",
    });
  } finally {
    isLoading.value = false;
  }
};
fetchData(undefined, 1);
</script>

<template>
  <div v-if="isLoading"><Loader /></div>
  <div class="card">
    <div class="card-header pb-0">
      <div class="d-flex align-items-center gap-2">
        <i class="ni ni-calendar-grid-58 text-primary text-md opacity-10"></i>
        <h6>Inventory Report</h6>
      </div>
      <div class="cta">
        <el-button-group>
          <el-button type="danger" @click="downloadPdf"
            ><i class="fa fa-file-pdf-o text-white me-1" aria-hidden="true"></i
            >PDF</el-button
          >
          <el-button type="success" @click="downloadExcel"
            ><i
              class="fa fa-file-excel-o text-white me-1"
              aria-hidden="true"
            ></i
            >Excel</el-button
          >
        </el-button-group>
      </div>
    </div>
    <div class="card-body px-0 pt-0 pb-2">
      <div v-if="tableData.length === 0">
        <empty-state-table />
      </div>

      <div class="table-responsive p-0">
        <table class="table align-items-center mb-0">
          <thead>
            <tr>
              <th
                class="text-center text-uppercase text-secondary text-xxs font-weight-bolder"
              >
                Product
              </th>
              <th
                class="text-left text-uppercase text-secondary text-xxs font-weight-bolder"
              >
                Code
              </th>
              <th
                class="text-left text-uppercase text-secondary text-xxs font-weight-bolder"
              >
                Current Stock
              </th>
              <th
                class="text-left text-uppercase text-secondary text-xxs font-weight-bolder"
              >
                Avg. Cost
              </th>
              <th
                class="text-left text-uppercase text-secondary text-xxs font-weight-bolder"
              >
                Asset Value
              </th>
              <th
                class="text-left text-uppercase text-secondary text-xxs font-weight-bolder"
              >
                % of Total Asset
              </th>
              <th
                class="text-left text-uppercase text-secondary text-xxs font-weight-bolder"
              >
                Sales Price
              </th>
              <th
                class="text-left text-uppercase text-secondary text-xxs font-weight-bolder"
              >
                Retail Value
              </th>
              <th
                class="text-left text-uppercase text-secondary text-xxs font-weight-bolder"
              >
                % of Total Retail
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(item, index) in tableData" :key="item.id + index">
              <td class="ps-4 text-xs text-left">{{ item.name }}</td>
              <td class="ps-4 text-xs text-left">{{ item.code }}</td>
              <td class="ps-4 text-xs text-left">
                {{ formatNumber(item.total_current_stock) }}
              </td>
              <td class="ps-4 text-xs text-left">
                {{ formatNumber(item.average_buying_price) }}
              </td>
              <td class="ps-4 text-xs text-left">
                {{ formatNumber(item.current_stock_buying_value) }}
              </td>
              <td class="ps-4 text-xs text-left">
                {{
                  formatNumber(
                    (item.current_stock_buying_value /
                      item.purchase_stock_value) *
                      100
                  )
                }}
              </td>
              <td class="ps-4 text-xs text-left">
                {{ formatNumber(item.average_selling_price) }}
              </td>
              <td class="ps-4 text-xs text-left">
                {{ formatNumber(item.current_stock_sale_value) }}
              </td>
              <td class="ps-4 text-xs text-left">
                {{
                  formatNumber(
                    (item.current_stock_sale_value /
                      item.purchase_stock_sale_value) *
                      100
                  )
                }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
    <div class="card-footer d-flex justify-content-center mb-3"></div>
  </div>
</template>

<style scoped></style>
